import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { GraduationCap, ArrowLeft, Download, Mail, Trash2, Eye, Edit, Trophy, MapPin } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';
import Footer from '../components/Footer';
import jsPDF from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyPredictions = () => {
  const navigate = useNavigate();
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, prediction: null });
  const [editFormData, setEditFormData] = useState({});
  const [counselingDates, setCounselingDates] = useState(null);
  
  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName');

  useEffect(() => {
    if (!userId) {
      navigate('/login');
      return;
    }

    fetchPredictions();
    fetchCounselingDates();
  }, [userId, navigate]);

  const fetchPredictions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/predictions/${userId}`);
      setPredictions(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load predictions",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCounselingDates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/counseling-dates`);
      setCounselingDates(response.data);
    } catch (error) {
      console.error('Failed to load counseling dates:', error);
    }
  };

  const handleEdit = (prediction) => {
    setEditDialog({ open: true, prediction });
    setEditFormData({
      rank: prediction.rank,
      examType: prediction.exam_type || prediction.examType,
      category: prediction.category,
      homeState: prediction.state || prediction.homeState,
    });
  };

  const handleUpdate = async () => {
    try {
      const updatedPrediction = {
        ...editDialog.prediction,
        rank: parseInt(editFormData.rank),
        exam_type: editFormData.examType,
        category: editFormData.category,
        state: editFormData.homeState,
        user_id: userId,
        predictions: editDialog.prediction.predictions || []
      };

      await axios.put(
        `${BACKEND_URL}/api/predictions/${editDialog.prediction.id}`,
        updatedPrediction
      );

      toast({
        title: "Success",
        description: "Prediction updated successfully"
      });
      
      setEditDialog({ open: false, prediction: null });
      fetchPredictions();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update prediction",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (predictionId) => {
    if (!window.confirm('Are you sure you want to delete this prediction?')) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/predictions/${predictionId}`);
      toast({ 
        title: "Deleted", 
        description: "Prediction deleted successfully" 
      });
      fetchPredictions();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete prediction",
        variant: "destructive"
      });
    }
  };

  const handleView = (prediction) => {
    navigate('/results', { state: { ...prediction, paymentConfirmed: true, savedPrediction: true } });
  };

  const handleDownloadPDF = async (prediction) => {
    try {
      const userName = localStorage.getItem('userName') || 'Student';
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(18);
      doc.setTextColor(37, 99, 235);
      doc.text('College Kampus - JEE Predictor', 105, 15, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text('Saved Prediction Report', 105, 22, { align: 'center' });
      
      // User info
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`Name: ${userName}`, 15, 32);
      doc.text(`Exam: ${prediction.exam_type || prediction.examType}`, 15, 38);
      doc.text(`Rank: ${prediction.rank}`, 15, 44);
      doc.text(`Category: ${prediction.category}`, 105, 32);
      doc.text(`State: ${prediction.state || prediction.homeState || 'N/A'}`, 105, 38);
      doc.text(`Date: ${new Date(prediction.created_at).toLocaleDateString()}`, 105, 44);
      
      // If predictions data exists, draw table manually
      if (prediction.predictions && prediction.predictions.length > 0) {
        let y = 55;
        const lineHeight = 6;
        
        // Table header
        doc.setFillColor(37, 99, 235);
        doc.rect(10, y, 190, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);
        doc.text('#', 12, y + 5);
        doc.text('College', 25, y + 5);
        doc.text('Branch', 110, y + 5);
        doc.text('Cutoff', 155, y + 5);
        doc.text('Chance', 180, y + 5);
        
        y += 10;
        doc.setTextColor(0, 0, 0);
        
        // Table rows
        prediction.predictions.slice(0, 30).forEach((result, index) => {
          if (y > 280) {
            doc.addPage();
            y = 20;
          }
          
          // Extract data with proper fallbacks (matching Results.jsx logic)
          const collegeName = (result.name || result.collegeName || result.college || 'Unknown').substring(0, 40);
          const branch = (result.branch || result.branchCode || 'N/A').substring(0, 20);
          const cutoff = (result.cutoff || result.cutoffRank || 'N/A').toString();
          const chance = (result.chance || 'Medium');
          
          doc.setFontSize(8);
          doc.text((index + 1).toString(), 12, y);
          doc.text(collegeName, 25, y);
          doc.text(branch, 110, y);
          doc.text(cutoff, 155, y);
          doc.text(chance, 180, y);
          
          doc.setDrawColor(200, 200, 200);
          doc.line(10, y + 2, 200, y + 2);
          
          y += lineHeight;
        });
      }
      
      doc.save(`JEE_Prediction_${userName.replace(/\s+/g, '_')}_${Date.now()}.pdf`);
      toast({ title: "Success!", description: "PDF downloaded successfully" });
    } catch (error) {
      console.error('PDF error:', error);
      toast({ title: "Error", description: "Failed to generate PDF", variant: "destructive" });
    }
  };

  const handleEmailReport = async (prediction) => {
    try {
      const userName = localStorage.getItem('userName') || 'Student';
      const userEmail = localStorage.getItem('userEmail');
      
      if (!userEmail) {
        toast({
          title: "Error",
          description: "Email address not found. Please update your profile.",
          variant: "destructive"
        });
        return;
      }
      
      const reportSummary = `
Exam: ${prediction.exam_type || prediction.examType}
Rank: ${prediction.rank}
Category: ${prediction.category}
Date: ${new Date(prediction.created_at).toLocaleDateString()}

Your saved prediction report is ready for download.
      `;
      
      const response = await axios.post(
        `${BACKEND_URL}/api/email/send-report`,
        null,
        {
          params: {
            user_email: userEmail,
            user_name: userName,
            report_summary: reportSummary
          }
        }
      );
      
      if (response.data.success) {
        toast({ title: "Email Sent", description: `Report sent to ${userEmail}` });
      }
    } catch (error) {
      toast({ 
        title: "Error", 
        description: "Failed to send email. Check SMTP settings.",
        variant: "destructive" 
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/')} className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={() => navigate('/referral')} 
                className="gap-2 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
              >
                <Trophy className="w-4 h-4" />
                Referrals
              </Button>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium">{userName}</p>
                <p className="text-xs text-slate-600">My Predictions</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">My Predictions</h1>
            <p className="text-slate-600">View and manage your saved college predictions</p>
          </div>

          {/* Counseling Dates Info Card - Enhanced */}
          {counselingDates && (
            <div className="mb-6 space-y-4">
              {/* JEE Counseling */}
              {counselingDates.jee_counseling?.active && counselingDates.jee_counseling?.rounds?.filter(r => r.active).length > 0 && (
                <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-blue-600" />
                      JEE/JoSAA Counseling Schedule
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {counselingDates.jee_counseling.rounds.filter(r => r.active).map((round, idx) => (
                      <div key={idx} className="bg-white p-4 rounded-lg border border-blue-200">
                        <h4 className="font-semibold text-blue-700 mb-3">{round.name}</h4>
                        <div className="grid md:grid-cols-2 gap-2 text-sm">
                          {round.registration_start && round.registration_end && (
                            <div>
                              <span className="font-medium">Registration: </span>
                              <span className="text-slate-600">
                                {new Date(round.registration_start).toLocaleDateString()} - {new Date(round.registration_end).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                          {round.choice_filling_start && round.choice_filling_end && (
                            <div>
                              <span className="font-medium">Choice Filling: </span>
                              <span className="text-slate-600">
                                {new Date(round.choice_filling_start).toLocaleDateString()} - {new Date(round.choice_filling_end).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                          {round.seat_allotment_date && (
                            <div>
                              <span className="font-medium">Seat Allotment: </span>
                              <span className="text-slate-600">{new Date(round.seat_allotment_date).toLocaleDateString()}</span>
                            </div>
                          )}
                          {round.payment_deadline && (
                            <div>
                              <span className="font-medium">Payment Deadline: </span>
                              <span className="text-slate-600">{new Date(round.payment_deadline).toLocaleDateString()}</span>
                            </div>
                          )}
                          {round.reporting_start && round.reporting_end && (
                            <div>
                              <span className="font-medium">Reporting: </span>
                              <span className="text-slate-600">
                                {new Date(round.reporting_start).toLocaleDateString()} - {new Date(round.reporting_end).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* State Counseling */}
              {counselingDates.state_counseling?.active && counselingDates.state_counseling?.states?.filter(s => s.active).length > 0 && (
                <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-purple-600" />
                      State Counseling Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {counselingDates.state_counseling.states.filter(s => s.active).map((state, idx) => (
                      <div key={idx} className="bg-white p-4 rounded-lg border border-purple-200">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-purple-700">{state.state_name} - {state.counseling_name}</h4>
                          {state.website_url && (
                            <a 
                              href={state.website_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Visit Website →
                            </a>
                          )}
                        </div>
                        <div className="grid md:grid-cols-2 gap-2 text-sm">
                          {state.registration_start && state.registration_end && (
                            <div>
                              <span className="font-medium">Registration: </span>
                              <span className="text-slate-600">
                                {new Date(state.registration_start).toLocaleDateString()} - {new Date(state.registration_end).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                          {state.choice_filling_start && state.choice_filling_end && (
                            <div>
                              <span className="font-medium">Choice Filling: </span>
                              <span className="text-slate-600">
                                {new Date(state.choice_filling_start).toLocaleDateString()} - {new Date(state.choice_filling_end).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                          {state.seat_allotment_date && (
                            <div>
                              <span className="font-medium">Seat Allotment: </span>
                              <span className="text-slate-600">{new Date(state.seat_allotment_date).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* General Notes */}
              {counselingDates.general_notes && (
                <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-700 leading-relaxed">{counselingDates.general_notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : predictions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-slate-600 mb-4">No predictions saved yet</p>
                <Button onClick={() => navigate('/')}>Create New Prediction</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {predictions.map((prediction) => (
                <Card key={prediction.id} className="hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-xl mb-2">
                          {prediction.exam_type || prediction.examType} - Rank {prediction.rank}
                        </CardTitle>
                        <CardDescription className="space-y-1">
                          <p><span className="font-medium">Category:</span> {prediction.category}</p>
                          <p><span className="font-medium">Counselling:</span> {prediction.counselling || 'N/A'}</p>
                          <p><span className="font-medium">State:</span> {prediction.state || prediction.homeState || 'N/A'}</p>
                          <p className="text-xs text-slate-500 mt-2">
                            <span className="font-medium">Prediction Date:</span> {prediction.created_at ? new Date(prediction.created_at).toLocaleString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'N/A'}
                          </p>
                        </CardDescription>
                      </div>
                      <Badge className="bg-green-100 text-green-800">Paid</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      <Button 
                        onClick={() => handleView(prediction)} 
                        className="gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View Results
                      </Button>
                      <Button 
                        onClick={() => handleEdit(prediction)} 
                        variant="outline" 
                        className="gap-2"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </Button>
                      <Button 
                        onClick={() => handleDownloadPDF(prediction)} 
                        variant="outline" 
                        className="gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download PDF
                      </Button>
                      <Button 
                        onClick={() => handleEmailReport(prediction)} 
                        variant="outline" 
                        className="gap-2"
                      >
                        <Mail className="w-4 h-4" />
                        Email Report
                      </Button>
                      <Button 
                        onClick={() => handleDelete(prediction.id)} 
                        variant="destructive" 
                        className="gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Prediction Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, prediction: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Prediction</DialogTitle>
            <DialogDescription>Update your prediction details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Rank</Label>
              <Input
                type="number"
                value={editFormData.rank || ''}
                onChange={(e) => setEditFormData({ ...editFormData, rank: e.target.value })}
              />
            </div>
            <div>
              <Label>Exam Type</Label>
              <Select
                value={editFormData.examType}
                onValueChange={(value) => setEditFormData({ ...editFormData, examType: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="JEE Main">JEE Main</SelectItem>
                  <SelectItem value="JEE Advanced">JEE Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select
                value={editFormData.category}
                onValueChange={(value) => setEditFormData({ ...editFormData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="General">General</SelectItem>
                  <SelectItem value="OBC">OBC</SelectItem>
                  <SelectItem value="SC">SC</SelectItem>
                  <SelectItem value="ST">ST</SelectItem>
                  <SelectItem value="EWS">EWS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Home State</Label>
              <Input
                value={editFormData.homeState || ''}
                onChange={(e) => setEditFormData({ ...editFormData, homeState: e.target.value })}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEditDialog({ open: false, prediction: null })}>
                Cancel
              </Button>
              <Button onClick={handleUpdate}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

export default MyPredictions;
